import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import CheckCircleIcon from 'mdi-react/CheckCircleIcon'
import CloseCircleIcon from 'mdi-react/CloseCircleIcon'
import MinusCircleIcon from 'mdi-react/MinusCircleIcon'
import PlusCircleIcon from 'mdi-react/PlusCircleIcon'
import { twJoin } from 'tailwind-merge'

import { Tooltip } from '../Tooltip'

export type OnOffBoardingStatus = {
  onOffBoarding: 'onboarding' | 'offboarding'
  onOffBoardingStatus: 'active' | 'completed' | 'cancelled'
}

const colors = [
  '#F1E5C3', // pastel yellow
  '#E9B9B7', // pastel pink
  '#A7B3D3', // pastel blue
  '#95D7D0', // pastel turquoise
  '#C8AABF', // pastel lavender
  '#E9C7AF', // pastel peach
  '#A6C5A6', // pastel sage
  '#EFD1D9', // pastel rose
  '#B5B5A4', // pastel grey
  '#D5C5DE', // pastel lilac
  '#C4A7A1', // pastel salmon
  '#7DE5B5', // pastel mint
  '#E5D087', // pastel apricot
  '#A3C6E8', // pastel sky blue
]

function convertStringToNumber(name: string): number {
  const reverseName = name.split(' ').reverse().join('')
  let hash = 0
  for (let i = 0; i < reverseName.length; i++) {
    hash = reverseName.charCodeAt(i) + ((hash << 5) - hash)
  }

  let number = hash % colors.length

  return Math.abs(number)
}

const initialsFromWords = (words: string[]) =>
  words
    .map(word => word[0])
    .join('')
    .toUpperCase()

const getInitials = (name: string): string => {
  const words = getWordsFromString(name)
  if (words.length <= 3) {
    return initialsFromWords(words)
  } else {
    const firstName = words.shift() || ''
    const lastName = words.pop() || ''
    const middleName = words.pop() || ''
    return initialsFromWords([firstName, middleName, lastName])
  }
}

const getWordsFromString = (str: string) => str.trim().split(' ')

export const NameAvatar: FC<{
  name: string | null
  id?: number
  status?: OnOffBoardingStatus
  whiteLabel?: WhiteLabel
  className?: string
  translate?: (key: string, params?: Record<string, string>) => string
}> = ({ name, id, status, whiteLabel, className, translate }) => {
  if (!name) {
    return null
  }
  const initials = getInitials(name)
  const color = colors[convertStringToNumber(name)]

  if (!status) {
    return (
      <div
        className={twJoin(
          'shadow-basic flex h-14 w-14 max-w-full items-center justify-center rounded-full border border-2 border-solid text-lg font-bold uppercase text-white',
          className
        )}
        style={{ backgroundColor: color }}
      >
        <span>{initials}</span>
      </div>
    )
  }

  const iconStyle =
    whiteLabel && ['telia', 'threeStepIT'].includes(whiteLabel)
      ? statusColors[whiteLabel]?.[status.onOffBoarding][status.onOffBoardingStatus]
      : statusColors.default?.[status.onOffBoarding][status.onOffBoardingStatus]

  return (
    <div className={twJoin('relative h-14 w-14 rounded-full', iconStyle?.bg, className)}>
      <div
        className="shadow-basic absolute inset-0 m-0.5 flex max-w-full items-center justify-center rounded-full border border-2 border-solid text-lg font-bold uppercase text-white"
        style={{ backgroundColor: color }}
      >
        <span>{initials}</span>
      </div>
      <div className="duration-250 absolute -bottom-0 -right-0.5 flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-white transition ease-in">
        {status.onOffBoarding === 'onboarding' && status.onOffBoardingStatus === 'active' && (
          <>
            <PlusCircleIcon
              data-tooltip-id={`active-onboarding-${id}`}
              data-tooltip-content={translate!('Onboarding in progress')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`active-onboarding-${id}`} style={{ zIndex: 100 }} />
          </>
        )}
        {status.onOffBoarding === 'onboarding' && status.onOffBoardingStatus === 'completed' && (
          <>
            <CheckCircleIcon
              data-tooltip-id={`newly-onboarded-${id}`}
              data-tooltip-content={translate!('Onboarding completed')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`newly-onboarded-${id}`} style={{ zIndex: 100 }} />
          </>
        )}
        {status.onOffBoarding === 'onboarding' && status.onOffBoardingStatus === 'cancelled' && (
          <>
            <PlusCircleIcon
              data-tooltip-id={`canceled-onboarding-${id}`}
              data-tooltip-content={translate!('Onboarding cancelled')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`canceled-onboarding-${id}`} style={{ zIndex: 100 }} />
          </>
        )}

        {status.onOffBoarding === 'offboarding' && status.onOffBoardingStatus === 'active' && (
          <>
            <MinusCircleIcon
              data-tooltip-id={`active-offboarding-${id}`}
              data-tooltip-content={translate!('Offboarding in progress')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`active-offboarding-${id}`} style={{ zIndex: 100 }} />
          </>
        )}
        {status.onOffBoarding === 'offboarding' && status.onOffBoardingStatus === 'completed' && (
          <>
            <CloseCircleIcon
              data-tooltip-id={`offboarded-${id}`}
              data-tooltip-content={translate!('Offboarded')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`offboarded-${id}`} style={{ zIndex: 100 }} />
          </>
        )}
        {status.onOffBoarding === 'offboarding' && status.onOffBoardingStatus === 'cancelled' && (
          <>
            <CloseCircleIcon
              data-tooltip-id={`cancelled-offboarding-${id}`}
              data-tooltip-content={translate!('Offboarding cancelled')}
              className={twJoin('w-full', iconStyle?.fill)}
            />
            <Tooltip id={`cancelled-offboarding-${id}`} style={{ zIndex: 100 }} />
          </>
        )}
      </div>
    </div>
  )
}

const statusColors: Partial<
  Record<
    WhiteLabel | 'default',
    Record<'onboarding' | 'offboarding', Record<string, Record<'bg' | 'fill', string>>>
  >
> = {
  default: {
    onboarding: {
      active: { bg: 'bg-blue', fill: 'fill-blue' },
      completed: { bg: 'bg-green-500', fill: 'fill-green-500' },
      cancelled: { bg: 'bg-red', fill: 'fill-red' },
    },
    offboarding: {
      active: { bg: 'bg-orange-500', fill: 'fill-orange-500' },
      completed: { bg: 'bg-gray-400', fill: 'fill-gray-400' },
      cancelled: { bg: 'bg-red', fill: 'fill-red' },
    },
  },
  telia: {
    onboarding: {
      active: { bg: 'bg-blue-500', fill: 'fill-blue-500' },
      completed: { bg: 'bg-telia-success', fill: 'fill-telia-success' },
      cancelled: { bg: 'bg-telia-red', fill: 'fill-telia-red' },
    },
    offboarding: {
      active: { bg: 'bg-orange-500', fill: 'fill-orange-500' },
      completed: { bg: 'bg-gray-500', fill: 'fill-gray-500' },
      cancelled: { bg: 'bg-telia-red', fill: 'fill-telia-red' },
    },
  },
  threeStepIT: {
    onboarding: {
      active: { bg: 'bg-threeStepIT-neutral', fill: 'fill-threeStepIT-neutral' },
      completed: { bg: 'bg-threeStepIT-success', fill: 'fill-threeStepIT-success' },
      cancelled: { bg: 'bg-threeStepIT-failure', fill: 'fill-threeStepIT-failure' },
    },
    offboarding: {
      active: { bg: 'bg-threeStepIT-warning', fill: 'fill-threeStepIT-warning' },
      completed: { bg: 'bg-threeStepIT-titanium', fill: 'fill-threeStepIT-titanium' },
      cancelled: { bg: 'bg-threeStepIT-failure', fill: 'fill-threeStepIT-failure' },
    },
  },
}
